const textEncoder = new TextEncoder()

function readUint16LE(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8)
}

function readUint32LE(bytes, offset) {
  return (
    bytes[offset]
    | (bytes[offset + 1] << 8)
    | (bytes[offset + 2] << 16)
    | (bytes[offset + 3] << 24)
  ) >>> 0
}

function writeUint16LE(bytes, offset, value) {
  bytes[offset] = value & 0xFF
  bytes[offset + 1] = (value >>> 8) & 0xFF
}

function writeUint32LE(bytes, offset, value) {
  bytes[offset] = value & 0xFF
  bytes[offset + 1] = (value >>> 8) & 0xFF
  bytes[offset + 2] = (value >>> 16) & 0xFF
  bytes[offset + 3] = (value >>> 24) & 0xFF
}

function concatUint8Arrays(chunks) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const output = new Uint8Array(totalLength)
  let offset = 0

  for (const chunk of chunks) {
    output.set(chunk, offset)
    offset += chunk.length
  }

  return output
}

function normalizeUint8Array(value) {
  if (value instanceof Uint8Array) {
    return value
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value)
  }

  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
  }

  return new Uint8Array(value || [])
}

const crc32Table = (() => {
  const table = new Uint32Array(256)

  for (let index = 0; index < 256; index += 1) {
    let current = index

    for (let bit = 0; bit < 8; bit += 1) {
      current = (current & 1)
        ? (0xEDB88320 ^ (current >>> 1))
        : (current >>> 1)
    }

    table[index] = current >>> 0
  }

  return table
})()

function crc32(bytes) {
  let current = 0xFFFFFFFF

  for (let index = 0; index < bytes.length; index += 1) {
    current = crc32Table[(current ^ bytes[index]) & 0xFF] ^ (current >>> 8)
  }

  return (current ^ 0xFFFFFFFF) >>> 0
}

export function readStoredZipEntries(input) {
  const bytes = normalizeUint8Array(input)
  let eocdOffset = -1

  for (let offset = bytes.length - 22; offset >= 0; offset -= 1) {
    if (readUint32LE(bytes, offset) === 0x06054B50) {
      eocdOffset = offset
      break
    }
  }

  if (eocdOffset < 0) {
    throw new Error('Unable to find ZIP end of central directory')
  }

  const entryCount = readUint16LE(bytes, eocdOffset + 10)
  const centralDirectoryOffset = readUint32LE(bytes, eocdOffset + 16)
  const textDecoder = new TextDecoder()
  const entries = []
  let offset = centralDirectoryOffset

  for (let index = 0; index < entryCount; index += 1) {
    if (readUint32LE(bytes, offset) !== 0x02014B50) {
      throw new Error('Invalid ZIP central directory header')
    }

    const compressionMethod = readUint16LE(bytes, offset + 10)
    if (compressionMethod !== 0) {
      throw new Error(`Unsupported ZIP compression method: ${compressionMethod}`)
    }

    const compressedSize = readUint32LE(bytes, offset + 20)
    const fileNameLength = readUint16LE(bytes, offset + 28)
    const extraFieldLength = readUint16LE(bytes, offset + 30)
    const fileCommentLength = readUint16LE(bytes, offset + 32)
    const localHeaderOffset = readUint32LE(bytes, offset + 42)
    const fileNameStart = offset + 46
    const fileNameEnd = fileNameStart + fileNameLength
    const name = textDecoder.decode(bytes.slice(fileNameStart, fileNameEnd))

    if (readUint32LE(bytes, localHeaderOffset) !== 0x04034B50) {
      throw new Error('Invalid ZIP local file header')
    }

    const localFileNameLength = readUint16LE(bytes, localHeaderOffset + 26)
    const localExtraFieldLength = readUint16LE(bytes, localHeaderOffset + 28)
    const dataStart = localHeaderOffset + 30 + localFileNameLength + localExtraFieldLength
    const dataEnd = dataStart + compressedSize

    entries.push({
      name,
      data: bytes.slice(dataStart, dataEnd)
    })

    offset = fileNameEnd + extraFieldLength + fileCommentLength
  }

  return entries
}

export function writeStoredZipEntries(entries) {
  const normalizedEntries = entries.map((entry) => ({
    name: String(entry?.name || ''),
    data: normalizeUint8Array(entry?.data)
  }))
  const localParts = []
  const centralParts = []
  let localOffset = 0

  for (const entry of normalizedEntries) {
    const nameBytes = textEncoder.encode(entry.name)
    const crc = crc32(entry.data)
    const localHeader = new Uint8Array(30 + nameBytes.length)

    writeUint32LE(localHeader, 0, 0x04034B50)
    writeUint16LE(localHeader, 4, 20)
    writeUint16LE(localHeader, 6, 0)
    writeUint16LE(localHeader, 8, 0)
    writeUint16LE(localHeader, 10, 0)
    writeUint16LE(localHeader, 12, 0)
    writeUint32LE(localHeader, 14, crc)
    writeUint32LE(localHeader, 18, entry.data.length)
    writeUint32LE(localHeader, 22, entry.data.length)
    writeUint16LE(localHeader, 26, nameBytes.length)
    writeUint16LE(localHeader, 28, 0)
    localHeader.set(nameBytes, 30)

    localParts.push(localHeader, entry.data)

    const centralHeader = new Uint8Array(46 + nameBytes.length)
    writeUint32LE(centralHeader, 0, 0x02014B50)
    writeUint16LE(centralHeader, 4, 20)
    writeUint16LE(centralHeader, 6, 20)
    writeUint16LE(centralHeader, 8, 0)
    writeUint16LE(centralHeader, 10, 0)
    writeUint16LE(centralHeader, 12, 0)
    writeUint16LE(centralHeader, 14, 0)
    writeUint32LE(centralHeader, 16, crc)
    writeUint32LE(centralHeader, 20, entry.data.length)
    writeUint32LE(centralHeader, 24, entry.data.length)
    writeUint16LE(centralHeader, 28, nameBytes.length)
    writeUint16LE(centralHeader, 30, 0)
    writeUint16LE(centralHeader, 32, 0)
    writeUint16LE(centralHeader, 34, 0)
    writeUint16LE(centralHeader, 36, 0)
    writeUint32LE(centralHeader, 38, 0)
    writeUint32LE(centralHeader, 42, localOffset)
    centralHeader.set(nameBytes, 46)
    centralParts.push(centralHeader)

    localOffset += localHeader.length + entry.data.length
  }

  const localData = concatUint8Arrays(localParts)
  const centralDirectory = concatUint8Arrays(centralParts)
  const eocd = new Uint8Array(22)

  writeUint32LE(eocd, 0, 0x06054B50)
  writeUint16LE(eocd, 4, 0)
  writeUint16LE(eocd, 6, 0)
  writeUint16LE(eocd, 8, normalizedEntries.length)
  writeUint16LE(eocd, 10, normalizedEntries.length)
  writeUint32LE(eocd, 12, centralDirectory.length)
  writeUint32LE(eocd, 16, localData.length)
  writeUint16LE(eocd, 20, 0)

  return concatUint8Arrays([localData, centralDirectory, eocd])
}
